import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { config } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { accountAtom } from "shared/lib/account";

import {
  type PlusWidgetsSampleContentType,
  PlusWidgetTheme,
} from "scmp-app/components/plus/plus-widget";
import { PlusWidgetHighlights } from "scmp-app/components/plus/plus-widget/plus-widget-highlights";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { ContentDiscoveryWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { plusWidgetWithoutAccess$key } from "scmp-app/queries/__generated__/plusWidgetWithoutAccess.graphql";

import {
  Container,
  HighlightsFromScmpPlus,
  Intro,
  IntroText,
  StyledLogoPlus,
  StyledPlusWidgetQuotes,
  Title,
  TryForFreeButtonLink,
} from "./styles";

type Props = {
  className?: string;
  pageType: "homepage" | "section" | "topic";
  reference: plusWidgetWithoutAccess$key;
  sampleContent: PlusWidgetsSampleContentType;
};

type Settings = {
  custom_text?: string;
};

export const PlusWidgetWithoutAccess: FunctionComponent<Props> = ({
  className,
  pageType,
  reference,
}) => {
  const { isLoggedIn } = useAtomValue(accountAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const hasScmpPlusUpgradeOptions = asyncRosettaState?.result?.user?.hasScmpPlusUpgradeOptions;
  const isEligibleForSCMPPlusFreeTrialSubscribeOffer =
    asyncRosettaState?.result?.user?.isEligibleForSCMPPlusFreeTrialSubscribeOffer;

  const ctaViewModel = useMemo(
    () =>
      resolvePlusWidgetCtaViewModel({
        hasScmpPlusUpgradeOptions,
        isEligibleForSCMPPlusFreeTrialSubscribeOffer,
        isLoggedIn,
      }),
    [isLoggedIn, hasScmpPlusUpgradeOptions, isEligibleForSCMPPlusFreeTrialSubscribeOffer],
  );

  const ga4ImpressionEvent = useCallback<() => ContentDiscoveryWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: ctaViewModel.callToAction,
        page_type: pageType,
        trigger_point: "content_discovery",
        widget_version: "non_subscriber",
      },
      subcategory: "entry_point",
    }),
    [ctaViewModel.callToAction, pageType],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<ContentDiscoveryWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  const handleTrialButtonClickEvent = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: ctaViewModel.callToAction,
        trigger_point: "content_discovery",
        widget_version: "non_subscriber",
      },
      subcategory: "entry_point",
    });
  }, [ctaViewModel.callToAction]);

  const data = useFragment(
    graphql`
      fragment plusWidgetWithoutAccess on Query
      @argumentDefinitions(
        applicationId: { type: "String!" }
        dailyPulseTypeUuId: { type: "String!" }
        highlightQueueName: { type: "String!" }
        plusNewsAgendaStartDate: { type: "Timestamp!" }
        plusNewsAgendaEndDate: { type: "Timestamp!" }
        plusNewsAgendaTypes: { type: "[String]!" }
      ) {
        ...plusWidgetDailyPulseQuery @arguments(dailyPulseTypeUuId: $dailyPulseTypeUuId)
        ...plusWidgetHighlightsQuery @arguments(queueName: $highlightQueueName)
        ...plusWidgetNewsAgendaQuery
          @arguments(
            applicationId: $applicationId
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
        highlightQueue: queue(filter: { name: $highlightQueueName }) {
          settings
        }
      }
    `,
    reference,
  );

  const customText = (data.highlightQueue?.settings as Settings)?.custom_text;
  const displayText = customText ?? "";

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <StyledLogoPlus />
      <Intro>
        <IntroText>
          SCMP Plus is an elite news platform that turns the day’s vital China news into something
          you can act on. To access our exclusive content you’ll need to subscribe.
        </IntroText>
        <TryForFreeButtonLink
          onClick={handleTrialButtonClickEvent}
          pathname={ctaViewModel.pathname}
        >
          {ctaViewModel.label}
        </TryForFreeButtonLink>
      </Intro>
      <StyledPlusWidgetQuotes />
      <HighlightsFromScmpPlus>
        <Title>{displayText}</Title>
        <PlusWidgetHighlights
          pageType={pageType}
          plusWidgetTheme={PlusWidgetTheme.DARK}
          reference={data}
        />
      </HighlightsFromScmpPlus>
    </Container>
  );
};

const resolvePlusWidgetCtaViewModel = ({
  hasScmpPlusUpgradeOptions,
  isEligibleForSCMPPlusFreeTrialSubscribeOffer,
  isLoggedIn,
}: {
  hasScmpPlusUpgradeOptions?: boolean;
  isEligibleForSCMPPlusFreeTrialSubscribeOffer?: boolean;
  isLoggedIn: boolean;
}): {
  callToAction: "plus trial" | "subscribe" | "upgrade";
  label: string;
  pathname: string;
} => {
  if (hasScmpPlusUpgradeOptions) {
    return {
      callToAction: "upgrade",
      label: "Upgrade",
      pathname: `${config.account.scmpAccountHost}/manage/subscription?upgrade=true`,
    };
  }

  if (isEligibleForSCMPPlusFreeTrialSubscribeOffer) {
    return {
      callToAction: "plus trial",
      label: "Try for free",
      pathname: "https://subscribe.scmp.com/plus",
    };
  }

  return {
    callToAction: "subscribe",
    label: isLoggedIn ? "Subscribe" : "Try for free or Upgrade",
    pathname: "https://subscribe.scmp.com/plus",
  };
};
